import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Plus, MessageSquareWarning, Filter, CheckCircle, Clock } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import Drawer from '../../components/common/Drawer/Drawer';
import SearchableSelect from '../../components/common/SearchableSelect';
import api from '../../api/axios';
import styles from './Complaints.module.css';

const Complaints = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    title: '',
    category: 'Plumbing',
    description: '',
    priority: 'Medium'
  });

  useEffect(() => {
    fetchComplaints();
  }, []);

  const fetchComplaints = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/users/complaints');
      setComplaints(data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch complaints');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post('/users/complaints', formData);
      setComplaints([data, ...complaints]);
      setFormData({ title: '', category: 'Plumbing', description: '', priority: 'Medium' });
      setIsDrawerOpen(false);
      toast.success('Complaint raised successfully!');
    } catch (error) {
      toast.error('Failed to raise complaint');
    }
  };

  return (
    <div className={styles.container}>
      <PageHeader 
        title="My Complaints" 
        breadcrumb={['Services', 'Complaints']} 
        actionButton={
          <button className={styles.addBtn} onClick={() => setIsDrawerOpen(true)}>
            <Plus size={16} /> Raise Complaint
          </button>
        }
      />

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.searchBox}>
            <input type="text" placeholder="Search complaints..." />
          </div>
        </div>

        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Complaint ID</th>
                <th>Title</th>
                <th>Category</th>
                <th>Date Raised</th>
                <th>Urgency</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className={styles.emptyState}>Loading complaints...</td>
                </tr>
              ) : complaints.map(comp => (
                <tr key={comp._id}>
                  <td><strong>#{comp._id.substring(comp._id.length - 6).toUpperCase()}</strong></td>
                  <td>{comp.title}</td>
                  <td><span className={styles.badgeCategory}>{comp.category}</span></td>
                  <td>{new Date(comp.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                  <td>
                    <span className={`${styles.badgeUrgency} ${styles[comp.priority.toLowerCase()]}`}>
                      {comp.priority}
                    </span>
                  </td>
                  <td>
                    <span className={`${styles.badgeStatus} ${styles[comp.status.replace(' ', '').toLowerCase()]}`}>
                      {comp.status === 'Resolved' ? <CheckCircle size={12} /> : <Clock size={12} />}
                      {comp.status}
                    </span>
                  </td>
                </tr>
              ))}
              {!loading && complaints.length === 0 && (
                <tr>
                  <td colSpan="6" className={styles.emptyState}>No complaints found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className={styles.mobileList}>
          {loading ? (
             <div className={styles.emptyState}>Loading complaints...</div>
          ) : complaints.length === 0 ? (
             <div className={styles.emptyState}>No complaints found.</div>
          ) : complaints.map(comp => (
            <div key={comp._id} className={styles.mobileCard}>
               <div className={styles.mobileCardHeader}>
                 <span className={styles.mobileId}>#{comp._id.substring(comp._id.length - 6).toUpperCase()}</span>
                 <span className={`${styles.badgeStatus} ${styles[comp.status.replace(' ', '').toLowerCase()]}`}>
                    {comp.status === 'Resolved' ? <CheckCircle size={12} /> : <Clock size={12} />}
                    {comp.status}
                 </span>
               </div>
               <div className={styles.mobileCardBody}>
                 <div className={styles.mobileTitle}>{comp.title}</div>
                 <div className={styles.mobileMeta}>
                   <span className={styles.badgeCategory}>{comp.category}</span>
                   <span className={`${styles.badgeUrgency} ${styles[comp.priority.toLowerCase()]}`}>{comp.priority}</span>
                 </div>
               </div>
               <div className={styles.mobileCardFooter}>
                 <small>Raised on {new Date(comp.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</small>
               </div>
            </div>
          ))}
        </div>
      </div>

      <Drawer 
        isOpen={isDrawerOpen} 
        onClose={() => setIsDrawerOpen(false)} 
        title="Raise Complaint"
      >
        <form className="drawerForm" onSubmit={handleSubmit}>
          <div className="formGroup">
            <label>Complaint Title</label>
            <input name="title" value={formData.title} onChange={handleChange} required placeholder="Brief title of the issue" />
          </div>
          <div className="formGroup">
            <label>Category</label>
            <SearchableSelect name="category" value={formData.category} onChange={handleChange} id="category-select">
              <option value="Plumbing">Plumbing</option>
              <option value="Electrical">Electrical</option>
              <option value="Cleaning">Cleaning</option>
              <option value="Security">Security</option>
              <option value="Other">Other</option>
            </SearchableSelect>
          </div>
          <div className="formGroup">
            <label>Priority Level</label>
            <select name="priority" value={formData.priority} onChange={handleChange}>
              <option value="Low">Low (Can wait a few days)</option>
              <option value="Medium">Medium (Needs attention soon)</option>
              <option value="High">High (Immediate attention required)</option>
            </select>
          </div>
          <div className="formGroup">
            <label>Detailed Description</label>
            <textarea 
              name="description" 
              value={formData.description} 
              onChange={handleChange} 
              required 
              rows="4" 
              placeholder="Describe the issue in detail..."
            ></textarea>
          </div>
          
          <div className="formActions">
            <button type="button" className="btnCancel" onClick={() => setIsDrawerOpen(false)}>Cancel</button>
            <button type="submit" className="btnSubmit">Submit Complaint</button>
          </div>
        </form>
      </Drawer>
    </div>
  );
};

export default Complaints;
